import { Channel, ChannelMember, ChannelMemberIndex, ChannelNotice, ChannelType, Message } from '../types';
import { apiFetch } from './apiClient';

// ============================================
// 응답 타입 정의
// ============================================

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface CreateChannelRequest {
  name?: string;
  type: ChannelType;
  description?: string;
  createdBy: string;
  members?: Array<{ id: string; name: string }>;
  /** 그룹방 이름을 직접 정하지 않았다 — 서버가 보는 사람마다 '나를 뺀 멤버' 이름으로 보여 준다 */
  nameAuto?: boolean;
}

interface CreateDMRequest {
  userId: string;
  targetUser: { id: string; name: string };
}

interface AddMembersRequest {
  members: Array<{ id: string; name: string }>;
}

interface SendMessageRequest {
  senderId: string;
  senderName: string;
  content: string;
  msgType?: 'text' | 'image' | 'file';
  replyToId?: number;
}

// ============================================
// API 헬퍼 함수
// ============================================

function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  return apiFetch<T>(endpoint, options);
}

// ============================================
// Channel Service
// ============================================

export const channelService = {
  // ----------------------------------------
  // 채널 조회
  // ----------------------------------------

  /**
   * 사용자의 채널 목록 조회
   * (공개 채널 + 참여 중인 비공개/그룹/DM)
   */
  async getChannels(userId: string): Promise<Channel[]> {
    return fetchApi<Channel[]>(`/channels?userId=${userId}`);
  },

  /**
   * 채널 상세 정보 조회
   */
  async getChannel(channelId: number, userId?: string): Promise<Channel> {
    const query = userId ? `?userId=${userId}` : '';
    return fetchApi<Channel>(`/channels/${channelId}${query}`);
  },

  /**
   * 채널 타입별 목록 조회
   */
  async getChannelsByType(userId: string, type: ChannelType): Promise<Channel[]> {
    const channels = await this.getChannels(userId);
    return channels.filter(ch => ch.type === type);
  },

  // ----------------------------------------
  // 채널 생성
  // ----------------------------------------

  /**
   * 새 채널 생성 (group, private)
   */
  async createChannel(request: CreateChannelRequest): Promise<Channel> {
    return fetchApi<Channel>('/channels', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * 그룹 채팅방 생성
   */
  async createGroupChannel(
    name: string,
    createdBy: string,
    members: Array<{ id: string; name: string }>,
    description?: string,
    nameAuto?: boolean
  ): Promise<Channel> {
    return this.createChannel({
      name,
      type: 'group',
      description,
      createdBy,
      members,
      ...(nameAuto ? { nameAuto: true } : {}),
    });
  },

  /**
   * DM 채널 찾기 또는 생성
   */
  async findOrCreateDM(request: CreateDMRequest): Promise<{ id: number; isNew: boolean }> {
    return fetchApi<{ id: number; isNew: boolean }>('/channels/dm', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * 나와의 채팅(self) 채널 찾기 또는 생성 (본인 1명만 멤버)
   */
  async findOrCreateSelf(userId: string): Promise<{ id: number; isNew: boolean }> {
    return fetchApi<{ id: number; isNew: boolean }>('/channels/self', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  },

  // ----------------------------------------
  // 채널 수정
  // ----------------------------------------

  /**
   * 채널 정보 수정
   */
  async updateChannel(
    channelId: number,
    updates: { name?: string; description?: string; isArchived?: boolean }
  ): Promise<Channel> {
    return fetchApi<Channel>(`/channels/${channelId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * 채널 아카이브 (삭제 대신)
   */
  async archiveChannel(channelId: number): Promise<Channel> {
    return this.updateChannel(channelId, { isArchived: true });
  },

  /**
   * 채널 공지 등록/해제 (messageId null이면 해제).
   * 반영은 서버가 쏘는 channel:notice-changed가 담당하므로 반환값으로 상태를 갱신하지 않아도 된다.
   */
  async setChannelNotice(
    channelId: number,
    messageId: number | null
  ): Promise<{ channelId: number; notice: ChannelNotice | null }> {
    return fetchApi<{ channelId: number; notice: ChannelNotice | null }>(`/channels/${channelId}/notice`, {
      method: 'PUT',
      body: JSON.stringify({ messageId }),
    });
  },

  // ----------------------------------------
  // 멤버 관리
  // ----------------------------------------

  /**
   * 대화방 검색용 참여자 인덱스 조회 — { channelId: [멤버 이름...] }
   *
   * 검색창을 처음 열 때 1회만 호출하고 클라이언트가 캐시한다(useChannelMemberIndex).
   * 채널 목록 응답에는 멤버 이름이 없어서(memberCount 숫자뿐) 참여자 검색에 필요하다.
   */
  async getMemberIndex(): Promise<ChannelMemberIndex> {
    return fetchApi<ChannelMemberIndex>('/channels/member-index');
  },

  /**
   * 채널 멤버 목록 조회
   */
  async getMembers(channelId: number): Promise<ChannelMember[]> {
    return fetchApi<ChannelMember[]>(`/channels/${channelId}/members`);
  },

  /**
   * 채널에 멤버 추가 (초대)
   */
  async addMembers(
    channelId: number,
    members: Array<{ id: string; name: string }>
  ): Promise<{ success: boolean; addedMembers: Array<{ userId: string; userName: string }> }> {
    return fetchApi(`/channels/${channelId}/members`, {
      method: 'POST',
      body: JSON.stringify({ members }),
    });
  },

  /**
   * 채널에서 나가기
   */
  async leaveChannel(channelId: number, userId: string): Promise<{ success: boolean }> {
    return fetchApi(`/channels/${channelId}/members/${userId}`, {
      method: 'DELETE',
    });
  },

  /**
   * 멤버 강퇴 (관리자용)
   */
  async removeMember(channelId: number, userId: string): Promise<{ success: boolean }> {
    return this.leaveChannel(channelId, userId);
  },

  /**
   * 멤버 역할 변경
   */
  async updateMemberRole(
    channelId: number,
    userId: string,
    role: 'admin' | 'member'
  ): Promise<{ success: boolean; role: string }> {
    return fetchApi(`/channels/${channelId}/members/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  },

  // ----------------------------------------
  // 읽음 상태
  // ----------------------------------------

  /**
   * 채널 읽음 표시 업데이트
   */
  async markAsRead(channelId: number, userId: string): Promise<{ success: boolean }> {
    return fetchApi(`/channels/${channelId}/read`, {
      method: 'PUT',
      body: JSON.stringify({ userId }),
    });
  },

  /**
   * 채팅방 고정/해제 (본인 설정, 신원은 서버가 JWT에서 취득)
   */
  async setPinned(
    channelId: number,
    isPinned: boolean
  ): Promise<{ success: boolean; isPinned: boolean; pinnedAt: string | null }> {
    return fetchApi(`/channels/${channelId}/pin`, {
      method: 'PUT',
      body: JSON.stringify({ isPinned }),
    });
  },

  /**
   * 채팅방별 알림 뮤트/해제 (본인 설정, 신원은 서버가 JWT에서 취득)
   */
  async setMuted(
    channelId: number,
    isMuted: boolean
  ): Promise<{ success: boolean; isMuted: boolean }> {
    return fetchApi(`/channels/${channelId}/mute`, {
      method: 'PUT',
      body: JSON.stringify({ isMuted }),
    });
  },

  // ----------------------------------------
  // 메시지
  // ----------------------------------------

  /**
   * 채널 메시지 목록 조회
   */
  async getMessages(channelId: number, limit = 100, before?: number): Promise<Message[]> {
    let endpoint = `/channels/${channelId}/messages?limit=${limit}`;
    if (before) {
      endpoint += `&before=${before}`;
    }
    return fetchApi<Message[]>(endpoint);
  },

  /**
   * 메시지 전송 (REST API)
   * 실시간은 소켓 사용 권장
   */
  async sendMessage(channelId: number, request: SendMessageRequest): Promise<Message> {
    return fetchApi<Message>(`/channels/${channelId}/messages`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * 메시지 삭제
   */
  async deleteMessage(messageId: number): Promise<{ success: boolean }> {
    return fetchApi(`/messages/${messageId}`, {
      method: 'DELETE',
    });
  },

  // ----------------------------------------
  // 유틸리티
  // ----------------------------------------

  /**
   * 채널 표시 이름 가져오기
   * DM의 경우 상대방 이름, 그룹은 채널명 또는 멤버 이름 조합
   */
  getDisplayName(channel: Channel, currentUserId: string, members?: ChannelMember[]): string {
    if (channel.name) {
      return channel.name;
    }

    if (channel.type === 'dm' && members) {
      const otherMember = members.find(m => m.userId !== currentUserId);
      return otherMember?.userName || '알 수 없음';
    }

    if (channel.type === 'group' && members) {
      const names = members
        .filter(m => m.userId !== currentUserId)
        .slice(0, 3)
        .map(m => m.userName);
      if (members.length > 4) {
        return `${names.join(', ')} 외 ${members.length - 4}명`;
      }
      return names.join(', ') || '그룹 채팅';
    }

    return '채팅방';
  },

  /**
   * 채널 아이콘 타입 가져오기
   */
  getChannelIcon(type: ChannelType): 'hash' | 'lock' | 'users' | 'user' {
    switch (type) {
      case 'public':
        return 'hash';
      case 'private':
        return 'lock';
      case 'group':
        return 'users';
      case 'dm':
        return 'user';
      default:
        return 'hash';
    }
  },

  /**
   * 안 읽은 메시지가 있는 채널 필터링
   */
  getUnreadChannels(channels: Channel[]): Channel[] {
    return channels.filter(ch => (ch.unreadCount ?? 0) > 0);
  },

  /**
   * 총 안 읽은 메시지 수
   */
  getTotalUnreadCount(channels: Channel[]): number {
    return channels.reduce((sum, ch) => sum + (ch.unreadCount ?? 0), 0);
  },
};

// ============================================
// React Hooks
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * 채널 멤버 목록 조회 훅
 */
export function useChannelMembers(channelId: number | null) {
  const [members, setMembers] = useState<ChannelMember[]>([]);
  const [loading, setLoading] = useState(false);
  // 채널을 빠르게 옮기면 이전 채널의 응답이 나중에 도착해 새 채널 화면에 옛 멤버가 들어간다
  // (멘션 후보가 통째로 오염됨). 요청 시점의 채널을 기록해 늦게 온 응답을 버린다.
  const requestedChannelRef = useRef<number | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!channelId) return;

    requestedChannelRef.current = channelId;
    setLoading(true);
    try {
      const data = await channelService.getMembers(channelId);
      if (requestedChannelRef.current !== channelId) return; // 그 사이 채널이 바뀜 — 폐기
      setMembers(data);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    } finally {
      if (requestedChannelRef.current === channelId) setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    // 채널이 바뀌면 이전 멤버를 즉시 비운다 — 새 목록이 올 때까지 옛 채널 멤버가 보이면 안 된다.
    setMembers([]);
    fetchMembers();
  }, [fetchMembers]);

  const addMembers = useCallback((newMembers: ChannelMember[]) => {
    setMembers(prev => [...prev, ...newMembers]);
  }, []);

  const removeMember = useCallback((userId: string) => {
    setMembers(prev => prev.filter(m => m.userId !== userId));
  }, []);

  return {
    members,
    loading,
    refetch: fetchMembers,
    addMembers,
    removeMember,
  };
}

/**
 * 그룹 채팅방 생성 훅
 */
export function useCreateGroupChannel() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createGroup = useCallback(async (
    name: string,
    createdBy: string,
    members: Array<{ id: string; name: string }>,
    description?: string
  ): Promise<Channel | null> => {
    setLoading(true);
    setError(null);

    try {
      const channel = await channelService.createGroupChannel(
        name,
        createdBy,
        members,
        description
      );
      return channel;
    } catch (err) {
      setError(err instanceof Error ? err.message : '채널 생성 실패');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createGroup,
    loading,
    error,
  };
}

/**
 * '나와의 채팅' 훅.
 *
 * DM용 openDM은 없다 — 사용자를 클릭해도 방을 만들지 않고 초안 DM(DRAFT_CHANNEL_ID)으로 열고,
 * 첫 메시지·통화 시점에 App.tsx의 materializeDraftDm이 findOrCreateDM을 직접 호출한다.
 * (클릭 즉시 생성하면 서버가 상대에게도 channel:new를 보내 빈 DM이 상대 목록에 쌓인다.
 *  self 방은 멤버가 나 1명이라 그 문제가 없어 예전 방식을 유지한다.)
 */
export function useDMChannel() {
  const [loading, setLoading] = useState(false);

  const openSelfChat = useCallback(async (
    userId: string
  ): Promise<number | null> => {
    setLoading(true);
    try {
      const result = await channelService.findOrCreateSelf(userId);
      return result.id;
    } catch (err) {
      console.error('Failed to open self chat:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    openSelfChat,
    loading,
  };
}
